import React, { useState, useRef, useEffect } from 'react';
import { Mic, MicOff, Zap, AlertCircle, CheckCircle, X, Camera, Loader2 } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

function QuickOrderInput({ onProdottiParsed, token }) {
  const [testo, setTesto] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [risultatoAnalisi, setRisultatoAnalisi] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [prodottiEsclusi, setProdottiEsclusi] = useState(new Set());
  const [isProcessingOCR, setIsProcessingOCR] = useState(false);
  const recognitionRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  // Inizializza riconoscimento vocale
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = false; // Solo risultati finali
      recognitionRef.current.lang = 'it-IT';

      recognitionRef.current.onresult = (event) => {
        // Prendi solo l'ultimo risultato finale
        const last = event.results.length - 1;
        const transcript = event.results[last][0].transcript;
        
        setTesto(prev => {
          // Aggiungi a capo se c'è già del testo
          return prev.trim() ? prev + '\n' + transcript.trim() : transcript.trim();
        });
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Errore riconoscimento vocale:', event.error);
        setIsRecording(false);
        toast.error('Errore nel riconoscimento vocale');
      };

      recognitionRef.current.onend = () => {
        setIsRecording(false);
      };
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  const toggleRecording = () => {
    if (!recognitionRef.current) {
      toast.error('Riconoscimento vocale non supportato dal browser');
      return;
    }

    if (isRecording) {
      recognitionRef.current.stop();
      setIsRecording(false);
    } else {
      recognitionRef.current.start();
      setIsRecording(true);
      toast.success('Inizia a parlare...');
    }
  };

  const analizzaTesto = async () => {
    if (!testo.trim()) {
      toast.error('Inserisci del testo da analizzare');
      return;
    }

    setIsAnalyzing(true);
    try {
      const response = await axios.post('/api/parse-order/analyze', 
        { testo },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setRisultatoAnalisi(response.data.data);
        setProdottiEsclusi(new Set()); // Reset esclusi
        setShowModal(true);
        
        if (response.data.data.prodottiTrovati.length === 0) {
          toast.error('Nessun prodotto riconosciuto');
        } else {
          toast.success(`${response.data.data.prodottiTrovati.length} prodotti riconosciuti`);
        }
      }
    } catch (error) {
      console.error('Errore analisi testo:', error);
      toast.error('Errore nell\'analisi del testo');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const confermaProdotti = () => {
    console.log('🔵 [QuickOrder] confermaProdotti chiamata');
    console.log('🔵 [QuickOrder] risultatoAnalisi:', risultatoAnalisi);
    
    if (risultatoAnalisi && risultatoAnalisi.prodottiTrovati.length > 0) {
      // Filtra prodotti esclusi
      const prodottiDaAggiungere = risultatoAnalisi.prodottiTrovati.filter((_, index) => !prodottiEsclusi.has(index));
      
      if (prodottiDaAggiungere.length === 0) {
        toast.error('Nessun prodotto selezionato');
        return;
      }
      
      console.log(`🔵 [QuickOrder] Chiamo onProdottiParsed con ${prodottiDaAggiungere.length} prodotti:`, prodottiDaAggiungere);
      
      onProdottiParsed(prodottiDaAggiungere);
      
      console.log('🔵 [QuickOrder] Callback eseguita, chiudo modal');
      setTesto('');
      setRisultatoAnalisi(null);
      setShowModal(false);
      setProdottiEsclusi(new Set());
    } else {
      console.log('❌ [QuickOrder] Nessun prodotto da confermare');
    }
  };

  const toggleEsclusioneProdotto = (index) => {
    setProdottiEsclusi(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const chiudiModal = () => {
    setShowModal(false);
    setRisultatoAnalisi(null);
    setProdottiEsclusi(new Set());
  };

  const processImageOCR = async (file) => {
    setIsProcessingOCR(true);
    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await axios.post('/api/ocr/recognize', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.success) {
        const nuovoTesto = response.data.text;
        // Aggiungi al testo esistente (o sostituisci)
        setTesto(prev => prev ? prev + '\n\n' + nuovoTesto : nuovoTesto);
        toast.success(`✅ Testo riconosciuto! (${nuovoTesto.split('\n').length} righe)`);
      } else {
        toast.error('Errore nel riconoscimento del testo');
      }
    } catch (error) {
      console.error('Errore OCR:', error);
      toast.error('Errore nel riconoscimento del testo');
    } finally {
      setIsProcessingOCR(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Seleziona un file immagine');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Immagine troppo grande (max 10MB)');
        return;
      }
      processImageOCR(file);
    }
    // Reset input per permettere di selezionare lo stesso file di nuovo
    e.target.value = '';
  };

  return (
    <>
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 mb-4 border-2 border-blue-200 shadow-sm">
        <div className="flex items-center gap-2 mb-2">
          <Zap className="h-5 w-5 text-blue-600" />
          <h3 className="font-semibold text-gray-800">Ordine Rapido AI</h3>
          <span className="text-xs text-gray-500 ml-auto">Es: "3 latte", "2kg farina", "acqua x5"</span>
        </div>

        <div className="relative">
          <textarea
            ref={textareaRef}
            value={testo}
            onChange={(e) => setTesto(e.target.value)}
            placeholder="Scrivi qui i prodotti... (es. 2 latte, 3 farina, 5 acqua naturale)"
            className="w-full p-3 pr-24 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 resize-none transition-all"
            rows={3}
            disabled={isRecording || isProcessingOCR}
          />
          
          <div className="absolute right-3 bottom-3 flex gap-2">
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isProcessingOCR || isRecording}
              className={`p-2 rounded-full transition-all ${
                isProcessingOCR
                  ? 'bg-purple-400 cursor-not-allowed'
                  : 'bg-purple-500 hover:bg-purple-600'
              } text-white shadow-lg`}
              title="Carica foto lista scritta a mano"
            >
              {isProcessingOCR ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Camera className="h-5 w-5" />
              )}
            </button>
            
            <button
              onClick={toggleRecording}
              disabled={isProcessingOCR}
              className={`p-2 rounded-full transition-all ${
                isRecording 
                  ? 'bg-red-500 hover:bg-red-600 animate-pulse' 
                  : 'bg-blue-500 hover:bg-blue-600'
              } text-white shadow-lg`}
              title={isRecording ? 'Ferma registrazione' : 'Inizia dettatura vocale'}
            >
              {isRecording ? (
                <MicOff className="h-5 w-5" />
              ) : (
                <Mic className="h-5 w-5" />
              )}
            </button>
          </div>
          
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleImageSelect}
            className="hidden"
          />
        </div>

        <div className="flex gap-2 mt-3">
          <button
            onClick={analizzaTesto}
            disabled={!testo.trim() || isAnalyzing}
            className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
          >
            <Zap className="h-4 w-4" />
            {isAnalyzing ? 'Analisi in corso...' : 'Analizza e Carica Prodotti'}
          </button>
          
          {testo.trim() && (
            <button
              onClick={() => setTesto('')}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors"
              title="Cancella tutto"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Modal Risultati */}
      {showModal && risultatoAnalisi && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="flex-shrink-0 bg-white border-b border-gray-200 p-3 flex justify-between items-center">
              <h3 className="text-lg font-bold text-gray-800">Risultati</h3>
              <button
                onClick={chiudiModal}
                className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-3">
              {/* Prodotti Trovati */}
              {risultatoAnalisi.prodottiTrovati.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <h4 className="text-sm font-semibold text-gray-800">
                      Riconosciuti ({risultatoAnalisi.prodottiTrovati.length})
                    </h4>
                  </div>
                  
                  <div className="space-y-2">
                    {risultatoAnalisi.prodottiTrovati.map((prodotto, index) => {
                      const matchPercentage = Math.round(prodotto.similarita * 100);
                      const isLowMatch = matchPercentage < 85;
                      const isEscluso = prodottiEsclusi.has(index);
                      
                      return (
                        <div
                          key={index}
                          className={`rounded-lg p-2 transition-all ${
                            isEscluso 
                              ? 'bg-gray-100 border border-gray-300 opacity-50'
                              : isLowMatch
                              ? 'bg-yellow-50 border border-yellow-400'
                              : 'bg-green-50 border border-green-200'
                          }`}
                        >
                          <div className="flex justify-between items-center gap-2">
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-medium truncate ${isEscluso ? 'text-gray-500 line-through' : 'text-gray-800'}`}>
                                {prodotto.quantita}x {prodotto.nome}
                              </p>
                              {prodotto.testoOriginale && (
                                <p className="text-xs text-gray-500 italic mt-0.5">
                                  Da: "{prodotto.testoOriginale}"
                                </p>
                              )}
                              {prodotto.note && (
                                <p className="text-xs text-gray-600 truncate mt-0.5">
                                  {prodotto.note}
                                </p>
                              )}
                            </div>
                            
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              <div className={`text-xs font-semibold px-1.5 py-0.5 rounded ${
                                isEscluso 
                                  ? 'text-gray-400'
                                  : isLowMatch 
                                  ? 'text-orange-600 bg-orange-100' 
                                  : 'text-green-600 bg-green-100'
                              }`}>
                                {matchPercentage}%
                              </div>
                              
                              <button
                                onClick={() => toggleEsclusioneProdotto(index)}
                                className={`p-1 rounded-full transition-all ${
                                  isEscluso
                                    ? 'bg-green-500 hover:bg-green-600 text-white'
                                    : 'bg-red-500 hover:bg-red-600 text-white'
                                }`}
                                title={isEscluso ? 'Includi' : 'Escludi'}
                              >
                                {isEscluso ? (
                                  <CheckCircle className="h-3.5 w-3.5" />
                                ) : (
                                  <X className="h-3.5 w-3.5" />
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Righe Non Riconosciute */}
              {risultatoAnalisi.righeNonRiconosciute.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="h-4 w-4 text-orange-600" />
                    <h4 className="text-sm font-semibold text-gray-800">
                      Non Trovati ({risultatoAnalisi.righeNonRiconosciute.length})
                    </h4>
                  </div>
                  
                  <div className="space-y-1.5">
                    {risultatoAnalisi.righeNonRiconosciute.map((riga, index) => (
                      <div
                        key={index}
                        className="bg-orange-50 border border-orange-200 rounded-lg p-2"
                      >
                        <p className="text-xs text-gray-700">"{riga}"</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            </div>
            
            {/* Azioni - Fixed Bottom */}
            <div className="flex-shrink-0 border-t border-gray-200 p-3 bg-white">
              <div className="flex gap-2">
                <button
                  onClick={confermaProdotti}
                  disabled={risultatoAnalisi.prodottiTrovati.length === 0 || prodottiEsclusi.size === risultatoAnalisi.prodottiTrovati.length}
                  className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-3 py-2.5 rounded-lg text-sm font-medium transition-colors"
                >
                  Conferma ({risultatoAnalisi.prodottiTrovati.length - prodottiEsclusi.size})
                </button>
                
                <button
                  onClick={chiudiModal}
                  className="px-4 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm font-medium transition-colors"
                >
                  Annulla
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default QuickOrderInput;

